/**
 * Locale plumbing shared by the auth context and the API client.
 *
 * The backend resolves the language a user should see (their own choice,
 * then team default, then organization default, then browser) and returns
 * it on /users/me as `effective_locale`. We remember it here so every API
 * request can carry it as Accept-Language and the document can declare it.
 */

const STORAGE_KEY = "locale";
const RTL_KEY = "locale_rtl";

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage may be unavailable (private mode, embedded widget); ignore.
  }
};

/** The locale the UI is currently rendering in, or the browser's. */
export const getCurrentLocale = () =>
  safeGet(STORAGE_KEY) ||
  (typeof navigator !== "undefined" && navigator.language) ||
  "en";

export const isCurrentLocaleRtl = () => safeGet(RTL_KEY) === "1";

/** Persist the resolved locale and reflect it on <html lang dir>. */
export const applyLocale = (locale, rtl = false) => {
  if (!locale) return;
  safeSet(STORAGE_KEY, locale);
  safeSet(RTL_KEY, rtl ? "1" : null);
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = locale;
    document.documentElement.dir = rtl ? "rtl" : "ltr";
  }
};

/** Apply the locale carried by a /users/me payload, if any. */
export const applyLocaleFromUser = (user) => {
  if (user && user.effective_locale) {
    applyLocale(user.effective_locale, !!user.effective_locale_rtl);
  }
};

export const clearLocale = () => {
  safeSet(STORAGE_KEY, null);
  safeSet(RTL_KEY, null);
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.dir = "ltr";
  }
};
